import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useExportDownload } from '@/modules/analytics/hooks/useExportDownload';
import type { MetricExportEntry } from '@/modules/analytics/types';
import { buildMetricJson } from '@/modules/analytics/utils/buildReportExport';
import { downloadMetricsExport } from '@/modules/analytics/utils/downloadMetricsExport';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';

import { ExportSubmenu } from './ExportSubmenu';

type Props = { entry: MetricExportEntry; onEdit?: () => void; onDelete?: () => void };

export const MetricActionsMenu = ({ entry, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();
  const { isExporting, download } = useExportDownload();

  const handleExport = (format: ExportFormat) =>
    download(() =>
      downloadMetricsExport(format, {
        entries: entry.hasData ? [entry] : [],
        filename: buildExportFilename(entry.name),
        title: 'report_metric',
        page: PageName.ANALYTICS_CUSTOM_REPORT_DETAIL,
        buildJson: () => buildMetricJson(entry),
      })
    );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          isLoading={isExporting}
          aria-label={t('analytics.reports_detail_metric_options')}
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ExportSubmenu onExport={handleExport} disabled={!entry.hasData} />
        {onEdit && (
          <DropdownMenuItem onSelect={onEdit}>
            <PencilIcon size={16} className="text-muted-foreground" />
            {`${t('common.edit')}...`}
          </DropdownMenuItem>
        )}
        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} variant="destructive">
              <Trash2Icon size={16} />
              {`${t('common.delete')}...`}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
